import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AdminAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Monkey-patch window.fetch ONCE so any /api/admin/* request automatically
 * carries the Authorization: Bearer <admin_token> header. Login/logout/verify
 * endpoints are exempt — the token is established by login itself.
 */
let _fetchPatched = false;
function patchFetch() {
  if (_fetchPatched || typeof window === 'undefined') return;
  const originalFetch = window.fetch.bind(window);
  const EXEMPT = new Set([
    '/api/admin/auth/login',
    '/api/admin/auth/logout',
    '/api/admin/auth/verify',
  ]);

  window.fetch = (input, init = {}) => {
    try {
      const url = typeof input === 'string' ? input : (input?.url || '');
      if (url.includes('/api/admin/')) {
        const u = new URL(url, window.location.origin);
        if (!EXEMPT.has(u.pathname)) {
          const token = localStorage.getItem('admin_token');
          if (token) {
            const headers = new Headers(init.headers || {});
            if (!headers.has('Authorization')) {
              headers.set('Authorization', `Bearer ${token}`);
            }
            init = { ...init, headers };
          }
        }
      }
    } catch { /* ignore URL parse errors */ }
    return originalFetch(input, init);
  };

  // Axios interceptor — same logic for any axios.*('/api/admin/...') call
  axios.interceptors.request.use((config) => {
    const url = config.url || '';
    if (url.includes('/api/admin/')) {
      const u = new URL(url, window.location.origin);
      if (!EXEMPT.has(u.pathname)) {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers = config.headers || {};
          if (!config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
      }
    }
    return config;
  });

  _fetchPatched = true;
}

// Patch at module load so the very first admin page fetch carries the token
patchFetch();

export function AdminAuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    const savedAdmin = localStorage.getItem('admin_user');
    
    if (token && savedAdmin) {
      try {
        const admin = JSON.parse(savedAdmin);
        setAdminUser(admin);
        setIsAuthenticated(true);
      } catch {
        logout();
      }
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('admin_token', data.token);
        localStorage.setItem('admin_user', JSON.stringify(data.admin));
        setAdminUser(data.admin);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        const error = await res.json();
        return { success: false, message: error.detail || 'Invalid credentials' };
      }
    } catch (error) {
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAdminUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    adminUser,
    loading,
    login,
    logout,
    checkAuth
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}

export default AdminAuthContext;
